import { FC, useContext, useEffect, useState } from 'react';
import { EditableTaskProperty } from '../../../Atoms/EditableProperties/EditableTaskProperty';
import EditableTaskListItem from '../../../Atoms/inputs/EditableTaskListItem';
import { AgendaItem } from '../../../Types/AgendaItem';
import AgendaType from '../../../Types/Enums/AgendaType';
import { MixpanelS } from '../../../Service/MixpanelS';
import { ConstantS } from '../../../Service/ConstantS';
import { ProfileContext } from '../../../Service/Context/ProfileContext';
import { parseDate } from '../../../Utils/dateUtils';

type Props = {
  task: AgendaItem;
  entityId?: number;
};

const germanDueDateFormatter = new Intl.DateTimeFormat('de-DE', {
  timeZone: 'Europe/Berlin',
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

const getDueDatePresentation = (value?: string) => {
  if (!value) return undefined;

  const date = parseDate(value);
  if (Number.isNaN(date.getTime())) return undefined;

  const getParts = (formattedDate: Date) => {
    const parts = germanDueDateFormatter.formatToParts(formattedDate);
    const getPart = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value ?? '';

    return {
      day: getPart('day'),
      month: getPart('month'),
      year: getPart('year'),
      hours: getPart('hour'),
      minutes: getPart('minute'),
    };
  };

  const dueDateParts = getParts(date);
  const todayParts = getParts(new Date());
  const dueDay = Date.UTC(Number(dueDateParts.year), Number(dueDateParts.month) - 1, Number(dueDateParts.day));
  const today = Date.UTC(Number(todayParts.year), Number(todayParts.month) - 1, Number(todayParts.day));
  const daysFromToday = Math.round((dueDay - today) / 86400000);
  const hasTime = Number(dueDateParts.hours) !== 0 || Number(dueDateParts.minutes) !== 0;
  const isToday = daysFromToday === 0;
  const isOverdue = daysFromToday < 0 || (isToday && hasTime && date.getTime() < Date.now());
  const dateText = `${dueDateParts.day}.${dueDateParts.month}.${dueDateParts.year}`;

  return {
    text: hasTime ? `${dateText} ${dueDateParts.hours}:${dueDateParts.minutes}` : dateText,
    daysFromToday,
    isToday,
    isOverdue,
  };
};

const TaskListItem: FC<Props> = ({ task, entityId }) => {
  const { name, completed, agendaType, lastModified } = task;

  const [value, setValue] = useState(completed);
  const { overdueTasks } = useContext(ProfileContext);

  const isTaskOverdue = () => getDueDatePresentation(task.dueDate)?.isOverdue ?? false;

  const handleChange = (val: AgendaItem) => {
    if (isTaskOverdue() && value !== val.completed) {
      if (val.completed) {
        overdueTasks.decrement();
      } else {
        overdueTasks.increment();
      }
    }

    setValue(val.completed);
    MixpanelS.track(ConstantS.TrackingEvents.TaskCompleted);
  };

  useEffect(() => {
    setValue(task.completed);
  }, [task]);

  switch (agendaType) {
    case AgendaType.ANSWER_REQUEST:
      return (
        <EditableTaskProperty
          entityId={entityId}
          handleChange={handleChange}
          value={{ completed: value, lastModified }}
          task={task}
          getDueDatePresentation={getDueDatePresentation}
          placeholder={name}
          editFormatter={EditableTaskListItem}
          editOnly
          labelFormatter={undefined}
        />
      );
    case AgendaType.FOLLOW_UP:
      return (
        <EditableTaskProperty
          entityId={entityId}
          handleChange={handleChange}
          value={{ completed: value, lastModified }}
          task={task}
          getDueDatePresentation={getDueDatePresentation}
          placeholder={name}
          editFormatter={EditableTaskListItem}
          editOnly
          labelFormatter={undefined}
        />
      );
    case AgendaType.STRING:
      return (
        <EditableTaskProperty
          entityId={entityId}
          handleChange={handleChange}
          value={{ completed: value, lastModified }}
          task={task}
          getDueDatePresentation={getDueDatePresentation}
          placeholder={name}
          editFormatter={EditableTaskListItem}
          editOnly
          labelFormatter={undefined}
        />
      );
    default:
      return (
        <EditableTaskProperty
          entityId={entityId}
          handleChange={handleChange}
          value={{ completed: value, lastModified }}
          task={task}
          getDueDatePresentation={getDueDatePresentation}
          placeholder={name}
          editFormatter={EditableTaskListItem}
          editOnly
          labelFormatter={undefined}
        />
      );
  }
};
export default TaskListItem;
